import type { ClientTool } from '../hooks/useClientTools';
import type { GraphQLClient } from './personaTools';
import {
  GetToolsDocument,
  McpCatalogInstancesDocument,
  McpCatalogToolsDocument,
  ConnectorToolsGroupsDocument,
  WorkflowToolsDocument,
  ListMcpServersDocument,
} from '@matrix-forge/graphql-client';

export const DISCOVER_TOOL_NAMES = ['list_available_tools', 'list_mcp_catalog'] as const;

/**
 * Static schema documentation embedded in the response so the LLM knows
 * how to build a valid `tools` JSON object for create_persona/update_persona.
 */
const TOOLS_CONFIG_SCHEMA = {
  description:
    'EXACT structure for the tools parameter in create_persona/update_persona. ' +
    'All top-level keys (predefined_names, allow_patterns, deny_patterns, ' +
    'system_tools, mcp_servers, workflow_tools, connectors) MUST be present. ' +
    'consent_mode: "auto" = run without asking, "manual" = ask user first. ' +
    'CRITICAL: For mcp_servers, you MUST list EVERY tool from list_available_tools ' +
    'in tool_configs with enabled:true and consent_mode. Empty tool_configs means NO tools.',
  predefined_names: [],
  allow_patterns: [],
  deny_patterns: [],
  system_tools: {
    '<tool_name>': { enabled: true, consent_mode: 'auto' },
  },
  mcp_servers: {
    '<server_slug>': {
      enabled: true,
      consent_mode: 'manual',
      tool_configs: {
        '<tool_name>': { enabled: true, consent_mode: 'auto' },
      },
    },
  },
  connectors: { '<connector_address>': { enabled: true } },
  workflow_tools: { '<workflow_id>': { enabled: true, auto_approve: false } },
};

/** Creates discovery client tools backed by GraphQL. */
export function createDiscoverTools(client: GraphQLClient): ClientTool[] {
  return [createListAvailableToolsTool(client), createListMcpCatalogTool(client)];
}

function createListAvailableToolsTool(client: GraphQLClient): ClientTool {
  return {
    name: 'list_available_tools',
    description: `Discover all tools available in the ecosystem across five categories:
system tools, MCP servers, connectors, workflows, and client tools.
Returns a structured catalog with tool names, descriptions, and a schema
showing how to configure them in create_persona/update_persona.
Optionally filter by category.`,
    parameters: {
      type: 'object',
      properties: {
        category: {
          type: 'string',
          enum: ['system', 'mcp', 'connectors', 'workflows', 'all'],
          description:
            'Filter to a specific tool source. Defaults to "all" which returns everything.',
        },
      },
    },
    handler: async (args: { category?: string }) => {
      const category = args.category ?? 'all';

      try {
        const result: Record<string, any> = {};

        // Build parallel queries for non-MCP sources
        const queries: Promise<void>[] = [];

        // --- System tools ---
        if (category === 'all' || category === 'system') {
          queries.push(
            client
              .query({ query: GetToolsDocument, fetchPolicy: 'network-only' })
              .then(({ data }) => {
                result.system_tools = (data?.tools ?? []).map((t: any) => ({
                  name: t.name,
                  description: t.description ?? null,
                  category: t.category ?? null,
                }));
              })
          );
        }

        // --- Connectors ---
        if (category === 'all' || category === 'connectors') {
          queries.push(
            client
              .query({
                query: ConnectorToolsGroupsDocument,
                fetchPolicy: 'network-only',
              })
              .then(({ data }) => {
                result.connectors = (data?.connectorToolsGroups ?? [])
                  .filter(Boolean)
                  .map((g: any) => ({
                    address: g.address,
                    name: g.name ?? null,
                    tools: (g.tools ?? []).map((t: any) => ({
                      name: t.name,
                      description: t.description ?? null,
                    })),
                  }));
              })
          );
        }

        // --- Workflows ---
        if (category === 'all' || category === 'workflows') {
          queries.push(
            client
              .query({
                query: WorkflowToolsDocument,
                fetchPolicy: 'network-only',
              })
              .then(({ data }) => {
                result.workflow_tools = (data?.workflowTools ?? []).map(
                  (t: any) => ({
                    name: t.name,
                    description: t.description ?? null,
                    workflowId: t.metadata?.workflowId ?? null,
                    workflowName: t.metadata?.workflowName ?? null,
                  })
                );
              })
          );
        }

        // --- MCP servers (instances first, then fan out for tools) ---
        if (category === 'all' || category === 'mcp') {
          queries.push(
            client
              .query({
                query: McpCatalogInstancesDocument,
                variables: { filter: { enabled: true } },
                fetchPolicy: 'network-only',
              })
              .then(async ({ data }) => {
                const instances = (data?.mcpCatalogInstances ?? []).filter(
                  (i: any) => i?.server
                );

                // Fan out to fetch tools for each enabled instance
                const serverEntries = await Promise.all(
                  instances.map(async (instance: any) => {
                    const slug = instance.server.slug;
                    try {
                      const { data: toolsData } = await client.query({
                        query: McpCatalogToolsDocument,
                        variables: { serverIdOrSlug: slug },
                        fetchPolicy: 'network-only',
                      });
                      return {
                        slug,
                        name: instance.server.name ?? slug,
                        tools: (toolsData?.mcpCatalogTools ?? [])
                          .filter(Boolean)
                          .map((t: any) => ({
                            name: t.name,
                            description: t.description ?? null,
                          })),
                      };
                    } catch {
                      // If fetching tools for a specific server fails, still include
                      // the server entry with an empty tools list
                      return {
                        slug,
                        name: instance.server.name ?? slug,
                        tools: [],
                      };
                    }
                  })
                );

                result.mcp_servers = serverEntries;
              })
          );
        }

        // Run all top-level queries in parallel
        await Promise.all(queries);

        // Always include client tools (informational only, not configurable)
        if (category === 'all') {
          result.client_tools = [
            {
              name: 'request_user_input',
              description: 'Present a form to collect user input',
            },
            {
              name: 'monitor_execution_status',
              description: 'Monitor workflow execution status',
            },
          ];
        }

        // Include the config schema so the LLM knows how to structure tools
        result.tools_config_schema = TOOLS_CONFIG_SCHEMA;

        return { result };
      } catch (err) {
        return {
          error: {
            message:
              err instanceof Error
                ? err.message
                : 'Failed to discover available tools',
          },
        };
      }
    },
  };
}

function createListMcpCatalogTool(client: GraphQLClient): ClientTool {
  return {
    name: 'list_mcp_catalog',
    description: `List MCP server templates available for the tenant. Use the "search"
parameter to find servers by name (e.g. "splunk", "github"). Always
search by name first rather than listing all 280+ servers.

Use "show" to filter by status: "all", "available", or "installed".

To install a server that requires auth:
1. Search for the server by name with this tool
2. Use request_user_input to collect credentials (authFields lists the field names)
3. Call setup_mcp_server with the templateId and collected credentials`,
    parameters: {
      type: 'object',
      properties: {
        search: {
          type: 'string',
          description:
            'Search servers by name (case-insensitive substring match). Always use this to narrow results instead of listing all servers. Example: "splunk", "github", "aws".',
        },
        show: {
          type: 'string',
          enum: ['all', 'available', 'installed'],
          description:
            'Filter by status. "all" (default), "available" (not yet enabled), "installed" (enabled only).',
        },
      },
    },
    handler: async (args: { search?: string; show?: string }) => {
      const show = args.show ?? 'all';
      const search = args.search?.toLowerCase();

      try {
        const { data } = await client.query({
          query: ListMcpServersDocument,
          fetchPolicy: 'network-only',
        });

        let filtered = data?.listMcpServers ?? [];

        // Apply status filter
        if (show === 'available') {
          filtered = filtered.filter((s: any) => !s.enabled && !s.comingSoon);
        } else if (show === 'installed') {
          filtered = filtered.filter((s: any) => s.enabled);
        }

        // Apply name search
        if (search) {
          filtered = filtered.filter(
            (s: any) =>
              s.templateName?.toLowerCase().includes(search) ||
              s.templateId?.toLowerCase().includes(search)
          );
        }

        // Return compact entries — authSchema and headersTemplate are only
        // needed at setup time (setup_mcp_server fetches them itself).
        const servers = filtered.map((s: any) => {
          const entry: Record<string, any> = {
            templateId: s.templateId,
            name: s.templateName,
            enabled: s.enabled,
            authRequired: s.authRequired,
            categories: s.categories ?? [],
            source: s.source ?? null,
            maturity: s.maturity ?? null,
          };

          if (s.templateDescription) {
            entry.description = s.templateDescription;
          }

          // Surface auth field names so the LLM knows what to collect
          // via request_user_input. We merge fields from four sources:
          // 1. authSchema.required (explicit required fields)
          // 2. authSchema.properties keys (all declared fields)
          // 3. headersTemplate {{secrets.xxx}} patterns (implicit fields)
          // 4. authRequirements[] from Construct plugins (sourceHeader-based)
          const authFields = new Set<string>();

          // From authSchema.required
          const required = s.authSchema?.required;
          if (Array.isArray(required)) {
            for (const f of required) authFields.add(f);
          }

          // From authSchema.properties keys
          const properties = s.authSchema?.properties;
          if (properties && typeof properties === 'object') {
            for (const key of Object.keys(properties)) {
              authFields.add(key);
            }
          }

          // From headersTemplate {{secrets.xxx}} patterns — many servers
          // have authRequired: false but still need credentials referenced
          // in their headers templates
          const headersTemplate = s.headersTemplate;
          if (headersTemplate && typeof headersTemplate === 'object') {
            for (const template of Object.values(headersTemplate)) {
              if (typeof template !== 'string') continue;
              const matches = (template as string).matchAll(
                /\{\{secrets\.(\w+)\}\}/g
              );
              for (const match of matches) {
                authFields.add(match[1]);
              }
            }
          }

          // From authRequirements (Construct-sourced servers) — these define
          // auth headers needed by the server even when authSchema is empty.
          // Each entry has sourceHeader (e.g. "X-Splunk-Token"), format hint, etc.
          const authRequirements: any[] = s.authRequirements ?? [];
          // Build a lookup for construct auth requirement details
          const authReqByHeader = new Map<string, any>();
          for (const req of authRequirements) {
            if (req?.sourceHeader) {
              authFields.add(req.sourceHeader);
              authReqByHeader.set(req.sourceHeader, req);
            }
          }

          if (authFields.size > 0) {
            // Build detailed field info so the LLM can create proper
            // request_user_input forms with labels and descriptions
            const fieldDetails: Array<{
              name: string;
              label: string;
              description?: string;
              isSecret: boolean;
            }> = [];

            for (const fieldName of authFields) {
              const prop = properties?.[fieldName];
              const authReq = authReqByHeader.get(fieldName);

              // Determine label: prefer authSchema title, then derive from
              // sourceHeader name (strip "X-" prefix, replace dashes with spaces)
              let label = prop?.title;
              if (!label && authReq) {
                label = fieldName
                  .replace(/^X-/i, '')
                  .replace(/^Connection-/i, '')
                  .replace(/-/g, ' ');
              }
              if (!label) {
                label = fieldName.replace(/_/g, ' ');
              }

              // Determine description: prefer authSchema, fall back to formatHint
              const description =
                prop?.description ?? authReq?.formatHint ?? undefined;

              const isSecret =
                prop?.secret_type === 'token' ||
                prop?.secret_type === 'basic_auth' ||
                prop?.format === 'password' ||
                /token|key|secret|password|auth/i.test(fieldName);

              fieldDetails.push({
                name: fieldName,
                label,
                ...(description && { description }),
                isSecret,
              });
            }

            entry.authFields = fieldDetails;
            // Override authRequired if we found fields but the flag is false
            if (!entry.authRequired) {
              entry.authRequired = true;
            }
          }

          return entry;
        });

        return {
          result: {
            count: servers.length,
            servers,
            setup_instructions:
              'To install a server that has authFields: 1) call request_user_input with form fields matching authFields (use the name, label, and description). IMPORTANT: for any field where isSecret is true, set type to "password" instead of "string" so the input is masked and credentials are not exposed in chat history. 2) call setup_mcp_server with BOTH the templateId AND the collected values as the "credentials" parameter. You MUST pass credentials — do NOT skip this step.',
          },
        };
      } catch (err) {
        return {
          error: {
            message:
              err instanceof Error
                ? err.message
                : 'Failed to list MCP catalog',
          },
        };
      }
    },
  };
}
